/**
 * PickupSection displays a carousel of featured hotels for pickup.
 * Handles selection and sorting of pickup hotels from area data and query response.
 */
"use client";

import { Button } from "@/components/atoms/button";
import EmblaCarousel from "@/components/atoms/emba-carousel";
import { useIsMobile } from "@/hooks/useIsMobile/useIsMobile";
import useJapanAreasStore from "@/store/hooks/useGlobal";
import {
  PickupQuery,
  UploadFile,
  JapanArea,
  JapanHotel,
} from "@/types/generated";
import { ISelectionResponse } from "@/types/mice";
import { CircleChevronRight } from "lucide-react";
import { useLocale } from "next-intl";
import React, { useMemo, useState } from "react";
import { PickupCard } from "./PickupCard";

export interface IPickup {
  id: string | undefined;
  picked_images_desktop: UploadFile[];
  picked_images_mobile: UploadFile[];
  hotel: string;
  city: string;
  slug: string;
  sortImage: number | null;
}

interface IFeedbackSectionProps {
  pickupData: PickupQuery;
  title: string;
  seeAllHotelLabel: string;
}
// Helper: Get display hotels map for quick lookup
const getDisplayHotelsMap = (
  displayHotels: ISelectionResponse[] | undefined,
): Map<string, number> => {
  const map = new Map<string, number>();
  if (displayHotels) {
    for (const item of displayHotels) {
      if (item.documentId) {
        map.set(item.documentId, item.order || 0);
      }
    }
  }
  return map;
};

// Helper: Get picked images for a hotel
const getPickedImages = (
  hotel: JapanHotel | undefined | null,
  imageType: "desktop" | "mobile",
): UploadFile[] => {
  const pickedList =
    imageType === "desktop"
      ? hotel?.datasources?.[0]?.slider_datasources?.[0]?.information
          ?.picked_images_desktop
      : hotel?.datasources?.[0]?.slider_datasources?.[0]?.information
          ?.picked_images_mobile;

  const sliderImages =
    imageType === "desktop"
      ? hotel?.hotel_images?.slider_desktop
      : hotel?.hotel_images?.slider_mobile;

  return (
    pickedList
      ?.map((picked: { documentId?: string }) => {
        const matched = sliderImages?.find(
          (img) => img && img.documentId === picked.documentId && img.image_url,
        );
        return matched?.image_url as UploadFile | undefined;
      })
      .filter(Boolean) || []
  );
};

// Helper: Transform hotel to pickup item
const transformHotelToPickupItem = (
  hotel: JapanHotel | undefined | null,
  areaName: string | undefined,
  displayHotelsMap: Map<string, number>,
): IPickup => {
  const sortImage = hotel?.documentId
    ? displayHotelsMap.get(hotel.documentId) || null
    : null;

  return {
    id: hotel?.documentId,
    picked_images_desktop: getPickedImages(hotel, "desktop"),
    picked_images_mobile: getPickedImages(hotel, "mobile"),
    hotel: hotel?.properties?.hotel_name || hotel?.item_name || "",
    city: areaName || "",
    slug: hotel?.slug || "",
    sortImage,
  };
};

// Helper: Filter and transform hotels from area
const getPickupItemsFromArea = (
  area: JapanArea | undefined | null,
  displayHotelsMap: Map<string, number>,
): IPickup[] => {
  const areaName = area?.item_name;

  return (
    area?.hotels
      ?.filter(
        (hotel) => hotel?.documentId && displayHotelsMap.has(hotel.documentId),
      )
      .map((hotel) =>
        transformHotelToPickupItem(hotel, areaName, displayHotelsMap),
      ) || []
  );
};

// Helper: Calculate slide item class name based on pickup count
const getSlideItemClassName = (pickupLength: number): string => {
  const twoPickupsClass =
    pickupLength === 2 ? "basis-[100%] md:basis-[40%]" : "";

  return pickupLength > 2
    ? "sm:pr-0 shrink-0 grow-0 basis-[80%] sm:basis-[45%] xl:basis-[25%]"
    : `shrink-0 grow-0 basis-[80%] w-full ${twoPickupsClass}`;
};

// Helper: Determine if navigation should be shown
const shouldShowNavigation = (
  pickupLength: number,
  isMobile: boolean,
): boolean => {
  if (pickupLength === 2) {
    return isMobile;
  }
  if (pickupLength > 2) {
    return true;
  }
  return false;
};

export const PickupSection: React.FC<IFeedbackSectionProps> = ({
  pickupData,
  title,
  seeAllHotelLabel,
}) => {
  const isMobile = useIsMobile();
  const { japanAreas } = useJapanAreasStore();
  const locale = useLocale();
  const [currentItem, setCurrentItem] = useState(0);

  /**
   * Selects and sorts pickup hotels based on display_hotels order.
   * (Now simplified since heavy logic was extracted.)
   */
  const selectedPickup = useMemo(() => {
    const displayHotels =
      pickupData.homeDatasourcesFeaturedHotelDatasource?.properties
        ?.display_hotels;
    const displayHotelsMap = getDisplayHotelsMap(displayHotels);

    return (japanAreas || [])
      .flatMap((area) => getPickupItemsFromArea(area, displayHotelsMap))
      .filter((item) => item?.id)
      .sort((a, b) => {
        if (!a || !b) return 0;
        return (a.sortImage ?? 0) - (b.sortImage ?? 0);
      });
  }, [japanAreas, pickupData]);

  const slideItemClassName = useMemo(
    () => getSlideItemClassName(selectedPickup.length),
    [selectedPickup.length],
  );

  const isShowNavigation = useMemo(
    () => shouldShowNavigation(selectedPickup.length, isMobile),
    [selectedPickup.length, isMobile],
  );

  const containerClassName = useMemo(() => {
    const baseClass =
      selectedPickup.length <= 2 && !isMobile
        ? "justify-center gap-8"
        : "items-center";
    const paddingClass = " pb-2";
    const centerClass = selectedPickup.length === 1 ? "justify-center" : "";

    return baseClass + paddingClass + centerClass;
  }, [selectedPickup.length, isMobile]);

  return (
    <div className="mx-auto w-full px-4 py-8 sm:px-6 lg:px-8">
      <h2 className="relative z-2 mb-8 text-center text-2xl font-[var(--font-semibold)] text-[var(--text-base-primary)] md:text-4xl">
        {title}
      </h2>

      <EmblaCarousel
        minScale={0.95}
        scaleStep={0.05}
        loop
        enableScaleAnimation
        configOverflow
        onSlideChange={(e) => setCurrentItem(e.selectedScrollSnap())}
        scaleTargetSelector=".pickup-card"
        containerClassName={containerClassName}
        showNavigation={isShowNavigation}
        navigationClassNames={{
          prev: "!flex",
          next: "!flex",
        }}
      >
        {selectedPickup.map((item, index) => (
          <div
            key={item.id}
            className={`${slideItemClassName} ${currentItem === index ? "lg:mx-5" : ""}`}
          >
            <div className="pickup-card rounded-lg shadow-[var(--shadow-feedback)]">
              <PickupCard item={item} locale={locale} />
            </div>
          </div>
        ))}
      </EmblaCarousel>

      <div className="flex w-full justify-center">
        <Button
          onClick={() => {
            globalThis.location.href = `/${locale}/hotels`;
          }}
          className="mt-2 w-full !justify-between rounded-sm py-6 font-[var(--font-medium)] md:w-[unset] md:justify-center lg:h-[55px] lg:min-w-[256px]"
        >
          {seeAllHotelLabel}
          <CircleChevronRight strokeWidth="1px" className="ml-10 h-4.5 w-4.5" />
        </Button>
      </div>
    </div>
  );
};
